import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  TrendingUp,
  ShoppingCart,
  People,
  AttachMoney,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { OrderWithClient, OrderStatistics } from "../../utils/adminOrderUtils";

interface DashboardOverviewProps {
  statistics: OrderStatistics | null;
  orders: OrderWithClient[];
  onRefresh: () => void;
}

const DashboardOverview: React.FC<DashboardOverviewProps> = ({
  statistics,
  orders,
  onRefresh,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const getStatusChip = (status: string) => {
    const statusColors: Record<
      string,
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning"
    > = {
      pending: "warning",
      "awaiting-info": "info",
      "in-progress": "primary",
      completed: "success",
      cancelled: "error",
    };

    return (
      <Chip
        label={status}
        color={statusColors[status] || "default"}
        size="small"
      />
    );
  };

  // Get recent orders (last 10)
  const recentOrders = orders.slice(0, 10);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h6">Dashboard Overview</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
        >
          Refresh Data
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr 1fr",
          },
          gap: 3,
          mb: 4,
        }}
      >
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <ShoppingCart color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" color="primary">
                Total Orders
              </Typography>
            </Box>
            <Typography variant="h4">{statistics?.totalOrders || 0}</Typography>
            <Typography variant="body2" color="text.secondary">
              All time orders
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <TrendingUp color="success" sx={{ mr: 1 }} />
              <Typography variant="h6" color="success.main">
                In Progress
              </Typography>
            </Box>
            <Typography variant="h4">
              {statistics?.inProgressOrders || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active orders
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <AttachMoney color="success" sx={{ mr: 1 }} />
              <Typography variant="h6" color="success.main">
                Total Revenue
              </Typography>
            </Box>
            <Typography variant="h4">
              {formatCurrency(statistics?.totalRevenue || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All time revenue
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <People color="info" sx={{ mr: 1 }} />
              <Typography variant="h6" color="info.main">
                Avg Order Value
              </Typography>
            </Box>
            <Typography variant="h4">
              {formatCurrency(statistics?.averageOrderValue || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Per order average
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Status Breakdown */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
          mb: 4,
        }}
      >
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Orders by Status
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {statistics?.ordersByStatus &&
                Object.entries(statistics.ordersByStatus).map(
                  ([status, count]) => (
                    <Box
                      key={status}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {getStatusChip(status)}
                        <Typography
                          variant="body2"
                          sx={{ textTransform: "capitalize" }}
                        >
                          {status.replace("-", " ")}
                        </Typography>
                      </Box>
                      <Typography variant="h6">{count}</Typography>
                    </Box>
                  )
                )}
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quick Stats
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Pending Orders:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {statistics?.pendingOrders || 0}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Completed Orders:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {statistics?.completedOrders || 0}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Completion Rate:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {statistics?.totalOrders
                    ? Math.round(
                        (statistics.completedOrders / statistics.totalOrders) *
                          100
                      )
                    : 0}
                  %
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Recent Orders */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Orders
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No orders found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  recentOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {order.id.substring(0, 8)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.clientName || order.clientEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.productType || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(order.status)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.totalAmount
                            ? formatCurrency(order.totalAmount)
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(order.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardOverview;
